import React from 'react';
import { 
  Calendar,
  MapPin,
  Users,
  Clock,
  CheckCircle2,
  Plane,
  Hotel,
  Ship,
  Car,
  Utensils,
  Mail,
  Phone
} from 'lucide-react';

const EditorialMagazineTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Helper function to format dates elegantly
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Distinguished Guest';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Massive, Edge-to-Edge */}
      <div className="relative min-h-[50vh] flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight tracking-tight">
            {quote.quoteTitle || 'Your Bespoke Journey'}
          </h1>
          {quote.travelDates?.start && (
            <p className="text-white/80 text-lg md:text-xl font-light tracking-widest uppercase">
              {formatDate(quote.travelDates.start)}
              {quote.travelDates.end && ` — ${formatDate(quote.travelDates.end)}`}
            </p>
          )}
        </div>
      </div>

      {/* Main Content - Magazine Layout with Massive Whitespace */}
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        
        {/* Introduction Section */}
        <div className="py-20">
          <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">Curated For</p>
          <h2 className="font-serif text-4xl text-gray-900 mb-8">
            {getClientName()}
          </h2>
          
          {(quote.travelers?.adults || quote.travelers?.children?.length) && (
            <div className="flex items-center gap-2 text-gray-600 text-sm tracking-wide">
              <Users className="w-4 h-4" />
              <span>
                {quote.travelers.adults || 0} Guest{quote.travelers.adults !== 1 ? 's' : ''}
                {quote.travelers.children?.length > 0 && ` · ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
              </span>
            </div>
          )}
        </div>

        {/* Itinerary - Editorial Style with Thin Dividers */}
        {quote.components && quote.components.length > 0 && (
          <div className="border-t border-gray-200">
            {quote.components.map((component, index) => (
              <div key={component.id || index} className="py-16 border-b border-gray-200">
                {/* Component Number */}
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-6">
                  Chapter {index + 1}
                </div>

                {/* Component Title - Large Serif */}
                <h3 className="font-serif text-3xl md:text-4xl text-gray-900 mb-6 leading-tight">
                  {component.title || `Experience ${index + 1}`}
                </h3>

                {/* Component Type Badge */}
                <div className="mb-8">
                  <span className="text-xs uppercase tracking-widest text-gray-500 border-b border-gray-300 pb-1">
                    {component.type || 'Exclusive Experience'}
                  </span>
                </div>

                {/* Component Details - Clean, Spacious */}
                {component.data && Object.keys(component.data).length > 0 && (
                  <div className="space-y-6 leading-relaxed tracking-wide text-gray-700">
                    {Object.entries(component.data).map(([key, value]) => {
                      if (!value || key === 'isOption' || key === 'optionDescription') return null;
                      
                      // Check if this is a price field
                      const isPriceField = key.toLowerCase().includes('price') || 
                                           key.toLowerCase().includes('cost') || 
                                           key.toLowerCase().includes('fee') ||
                                           key.toLowerCase().includes('total') ||
                                           key.toLowerCase().includes('deposit');
                      const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                      
                      let displayValue = String(value);
                      if (isPriceField && isNumeric) {
                        displayValue = formatCurrency(parseFloat(value));
                      }
                      
                      return (
                        <div key={key} className="flex flex-col md:flex-row md:items-baseline gap-2">
                          <dt className="text-xs uppercase tracking-widest text-gray-400 md:w-48 flex-shrink-0">
                            {key.replace(/_/g, ' ')}
                          </dt>
                          <dd className="text-base text-gray-900 font-light">
                            {displayValue}
                          </dd>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Component Photos */}
                {component.photos && component.photos.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-100">
                    <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4 flex items-center gap-2">
                      <span>📸</span> Gallery
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {component.photos.map((photo, photoIndex) => (
                        <div 
                          key={photoIndex} 
                          className="relative group cursor-pointer overflow-hidden bg-white"
                          onClick={() => window.open(photo.url, '_blank')}
                        >
                          <img 
                            src={photo.url} 
                            alt={photo.name || 'Component photo'} 
                            className="w-full h-auto object-contain"
                            style={{ maxHeight: '300px' }}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-medium">{photo.name || 'View Photo'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Documents */}
                {component.documents && component.documents.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                      <span>📋</span> Documents
                    </h4>
                    <div className="space-y-2">
                      {component.documents.map((doc, docIndex) => (
                        <a 
                          key={docIndex}
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm"
                        >
                          <span>📄</span>
                          <span>{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Price - Elegant Display */}
                <div className="mt-12 pt-8 border-t border-gray-100">
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs uppercase tracking-widest text-gray-400">Investment</span>
                    <span className="font-serif text-2xl text-gray-900">
                      {formatCurrency(component.price)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Notes Section - Editorial Style */}
        {quote.notes && (
          <div className="py-20 border-t border-gray-200">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-6">Essential Details</p>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed tracking-wide font-light whitespace-pre-wrap">
                {quote.notes}
              </p>
            </div>
          </div>
        )}

        {/* Investment Summary - Minimal, Elegant */}
        <div className="py-20 border-t border-gray-200">
          <div className="space-y-12">
            {/* Total Investment */}
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-4">Total Investment</p>
              <p className="font-serif text-5xl md:text-6xl text-gray-900">
                {formatCurrency(quote.totalPrice)}
              </p>
            </div>

            {/* Deposit Information */}
            {quote.depositAmount > 0 && (
              <div className="pt-8 border-t border-gray-100">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">Initial Deposit</p>
                    <p className="font-serif text-3xl text-gray-900">
                      {formatCurrency(quote.depositAmount)}
                    </p>
                  </div>
                  
                  {quote.depositDueDate && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">Due Date</p>
                      <p className="text-xl text-gray-700 font-light">
                        {formatDate(quote.depositDueDate)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Button - Minimal Design */}
            {onPayment && (
              <div className="pt-12">
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full bg-gray-900 hover:bg-gray-800 text-white font-light tracking-widest uppercase text-sm py-6 px-8 transition-all duration-300 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isAgentPreview ? 'Preview Mode' : 'Reserve Your Journey'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs text-gray-400 mt-6 tracking-wide">
                    Secure payment · Complimentary concierge service included
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-16 border-t border-gray-200 pt-12">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-light text-gray-900 mb-2 tracking-wide">
                {quote.agent_name || 'Your Travel Curator'}
              </h3>
              {quote.agent_business_name && (
                <p className="text-gray-500 text-sm uppercase tracking-widest mb-6">{quote.agent_business_name}</p>
              )}
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_email && quote.agent_phone && (
                  <span className="hidden sm:block text-gray-300">|</span>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer - Minimal Branding */}
        <div className="py-16 border-t border-gray-200 text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400">
            Bespoke Travel Experiences
          </p>
        </div>
      </div>
    </div>
  );
};

export default EditorialMagazineTemplate;
